/**
 * Accueil. Quatre intentions : jouer tout de suite, creer, rejoindre,
 * parcourir : dont une seule est primaire (la partie rapide), le reste en
 * ghost, conforme au design system.
 *
 * La partie rapide est en tete et en or parce qu'elle est la seule qui ne
 * demande rien : ni code, ni reglage, ni attente. Le serveur place le joueur
 * dans une partie en cours de remplissage, ou lui donne un adversaire
 * automatique s'il est seul, et la partie demarre aussitot.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  ROOM_CODE_LENGTH,
  isValidRoomCode,
  normalizeRoomCode,
  randomNickname,
  sanitizeNickname,
} from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { useUiStore } from '../store/uiStore.js';
import { connection } from '../net/connection.js';
import { ArtworkBackdrop } from '../ui/ArtworkBackdrop.js';
import { Screen } from '../ui/Screen.js';
import { navigate } from '../router.js';
import { useDismissOnBack } from '../ui/useDismissOnBack.js';
import { CloseIcon } from '../ui/icons.js';
import { LegalFooter, SiteFooter } from '../ui/SiteFooter.js';
import { HomeBelowTheFold } from './HomeBelowTheFold.js';
import { showPreroll } from '../ads/adapter.js';
import { trackCta } from '../analytics.js';

export function HomeScreen(): ReactElement {
  const t = useT();
  const nickname = useUiStore((state) => state.nickname);
  const setNickname = useUiStore((state) => state.setNickname);

  // Deux feuilles distinctes : creer et rejoindre sont deux intentions
  // differentes, les melanger obligeait a choisir apres coup.
  const [sheet, setSheet] = useState<'none' | 'create' | 'join'>('none');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Coche par defaut : une partie creee a vocation a se remplir. Qui veut jouer
  // entre amis decoche, et le code d'invitation reste le seul chemin d'entree.
  const [publicRoom, setPublicRoom] = useState(true);
  const codeRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (sheet === 'join') codeRef.current?.focus();
  }, [sheet]);

  // Un pseudo est propose des l'arrivee : le champ vide etait la seule chose
  // entre le visiteur et sa premiere partie, et le bouton principal refusait de
  // partir sans qu'on comprenne toujours pourquoi. Il reste modifiable, et
  // persiste comme un pseudo saisi a la main.
  useEffect(() => {
    if (sanitizeNickname(useUiStore.getState().nickname) !== null) return;
    setNickname(randomNickname());
  }, [setNickname]);

  const requireNickname = useCallback((): string | null => {
    const clean = sanitizeNickname(nickname);
    if (clean === null) {
      setError(t('home.nicknameRequired'));
      return null;
    }
    setError(null);
    return clean;
  }, [nickname, t]);

  // Preroll avant la premiere partie : le seul moment ou l'attente est deja
  // acquise. Il ne bloque jamais la creation, meme s'il echoue.
  const createRoom = useCallback(() => {
    const clean = requireNickname();
    if (clean === null) return;
    void showPreroll().finally(() =>
      connection.join(clean, { create: true, visibility: publicRoom ? 'public' : 'private' }),
    );
  }, [requireNickname, publicRoom]);

  /**
   * Partie rapide : aucun ecran intermediaire, aucun reglage. Le serveur choisit
   * le salon (ou en ouvre un avec un adversaire automatique) et la partie part
   * toute seule.
   */
  const quickPlay = useCallback(() => {
    trackCta('quick_play');
    const clean = requireNickname();
    if (clean === null) return;
    void showPreroll().finally(() => connection.join(clean));
  }, [requireNickname]);

  const closeSheet = useCallback(() => setSheet('none'), []);

  // Le bouton RETOUR du navigateur ferme la feuille : c'est le reflexe, et
  // c'est ce qui manquait le plus.
  useDismissOnBack(sheet !== 'none', closeSheet);

  // Echap la ferme aussi : meme geste au clavier.
  useEffect(() => {
    if (sheet === 'none') return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeSheet();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheet, closeSheet]);

  const joinByCode = useCallback(() => {
    const clean = requireNickname();
    if (clean === null) return;
    const normalized = normalizeRoomCode(code);
    if (!isValidRoomCode(normalized)) return;
    connection.join(clean, { code: normalized });
  }, [code, requireNickname]);

  return (
    <Screen name="home" scroll>
      <ArtworkBackdrop />

      <main className="home">
        <header className="home__header">
          <h1 className="home__title">{t('app.title')}</h1>
          <p className="home__tagline">{t('app.tagline')}</p>
        </header>

        <div className="home__form">
          <label className="field">
            <span className="field__label">{t('home.nickname')}</span>
            <input
              className="field__input"
              value={nickname}
              maxLength={24}
              placeholder={t('home.nicknamePlaceholder')}
              onChange={(event) => {
                setNickname(event.target.value);
                setError(null);
              }}
              onKeyDown={(event) => {
                // Entree suit le bouton primaire, qui est desormais la partie
                // rapide : c'est l'action que ce champ prepare.
                if (event.key === 'Enter') quickPlay();
              }}
            />
          </label>
          {error !== null && <p className="field__error">{error}</p>}

          <button type="button" className="button-primary button-block" onClick={quickPlay}>
            {t('home.quickPlay')}
          </button>

          <button
            type="button"
            className="button-ghost button-block"
            onClick={() => {
              trackCta('create');
              setSheet('create');
            }}
          >
            {t('home.create')}
          </button>
          <button
            type="button"
            className="button-ghost button-block"
            onClick={() => {
              trackCta('join');
              setSheet('join');
            }}
          >
            {t('home.join')}
          </button>
          <button
            type="button"
            className="button-ghost button-block"
            onClick={() => {
              trackCta('browse');
              navigate('/browse');
            }}
          >
            {t('home.browse')}
          </button>
        </div>

        {/* Le pied de page reste ICI, dans le premier ecran, exactement ou il
            etait avant l'ajout du contenu editorial. Le descendre en bas de
            page aurait recentre le bloc et fait remonter les boutons de
            quelques pixels : le premier ecran ne doit pas bouger. */}
        <SiteFooter />
      </main>

      <HomeBelowTheFold />

      {/* Tout en bas du document, apres le contenu editorial : on ne cherche
          pas les mentions legales en arrivant. */}
      <LegalFooter />

      {sheet !== 'none' && (
        <>
          <button
            type="button"
            className="sheet__scrim"
            aria-label={t('common.close')}
            onClick={closeSheet}
          />
          <section
            className="sheet"
            role="dialog"
            aria-label={sheet === 'create' ? t('home.create') : t('home.join')}
          >
            <div className="sheet__handle" />
            {/* Une sortie VISIBLE. Le fond sombre se ferme aussi au clic, mais
                rien ne le dit, et sur telephone la feuille couvre presque tout
                l'ecran : il ne restait quasiment rien a viser. */}
            <button
              type="button"
              className="sheet__close"
              aria-label={t('common.close')}
              onClick={closeSheet}
            >
              <CloseIcon />
            </button>
            <h2 className="sheet__title">
              {sheet === 'create' ? t('home.create') : t('home.join')}
            </h2>

            {sheet === 'join' ? (
              <div className="sheet__row">
                <input
                  ref={codeRef}
                  className="field__input field__input--code mono-tabular"
                  value={code}
                  inputMode="text"
                  autoCapitalize="characters"
                  spellCheck={false}
                  maxLength={ROOM_CODE_LENGTH}
                  placeholder={t('home.codePlaceholder')}
                  onChange={(event) => setCode(normalizeRoomCode(event.target.value))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') joinByCode();
                  }}
                />
                <button
                  type="button"
                  className="button-primary"
                  disabled={!isValidRoomCode(normalizeRoomCode(code))}
                  onClick={joinByCode}
                >
                  {t('home.join')}
                </button>
              </div>
            ) : (
              <>
                <label className="sheet__toggle">
                  <input
                    type="checkbox"
                    checked={publicRoom}
                    onChange={(event) => setPublicRoom(event.target.checked)}
                  />
                  <span>
                    {t('home.publicRoom')}
                    <small>{t('home.publicRoomHint')}</small>
                  </span>
                </label>

                <button type="button" className="button-primary button-block" onClick={createRoom}>
                  {t('home.create')}
                </button>
              </>
            )}
          </section>
        </>
      )}
    </Screen>
  );
}
